/**
 * name: @tresjs/core
 * version: v3.5.0
 * (c) 2023
 * description: Declarative ThreeJS using Vue Components
 * author: Alvaro Saburido <hola@alvarosaburido.dev> (https://github.com/alvarosabu/)
 */
import { ref as L, computed as A, watchEffect as D, onUnmounted as N, shallowRef as x, watch as ee, reactive as se, readonly as ie, provide as z, inject as le, createRenderer as ue, defineComponent as q, useSlots as ce, getCurrentInstance as U, onMounted as fe, openBlock as pe, createElementBlock as me, normalizeClass as de, normalizeStyle as ve, h as J, Fragment as ge } from "vue";
import * as ye from "three";
import { PerspectiveCamera as I, OrthographicCamera as we, Camera as Ce, Clock as Pe, Vector3 as he, Color as H, REVISION as Ee, SRGBColorSpace as _e, ACESFilmicToneMapping as Me, PCFSoftShadowMap as Ae, WebGLRenderer as $, LoadingManager as ke, TextureLoader as De, Vector2 as be, Raycaster as xe, BufferAttribute as Be, Scene as Le } from "three";
import { createEventHook as B, useRafFn as Oe, toValue as E, unrefElement as Te, useDevicePixelRatio as Se, usePointer as Re, useElementBounding as He, useWindowSize as je, useElementSize as Fe } from "@vueuse/core";
const $e = ({ sizes: e, scene: a }) => {
  const t = L([]), r = A(
    () => t.value[0]
  ), n = (s, f = !1) => {
    t.value.some(({ uuid: i }) => i === s.uuid) || (f ? u(s) : t.value.push(s));
  }, o = (s) => {
    t.value = t.value.filter(({ uuid: f }) => f !== s.uuid);
  }, u = (s) => {
    const f = s instanceof Ce ? s : t.value.find((m) => m.uuid === s);
    if (!f)
      return;
    const i = t.value.filter(({ uuid: m }) => m !== f.uuid);
    t.value = [f, ...i];
  };
  return D(() => {
    e.aspectRatio.value && t.value.forEach((s) => {
      s instanceof I && (s.aspect = e.aspectRatio.value), (s instanceof I || s instanceof we) && s.updateProjectionMatrix();
    });
  }), a.userData.tres__registerCamera = n, a.userData.tres__deregisterCamera = o, N(() => {
    t.value = [];
  }), {
    camera: r,
    cameras: t,
    registerCamera: n,
    deregisterCamera: o,
    setCameraActive: u
  };
}, te = B(), re = B(), W = B(), b = new Pe();
let j = 0, F = 0;
const { pause: ze, resume: Ie, isActive: Ge } = Oe(
  () => {
    te.trigger({ delta: j, elapsed: F, clock: b }), re.trigger({ delta: j, elapsed: F, clock: b }), W.trigger({ delta: j, elapsed: F, clock: b });
  },
  { immediate: !1 }
);
W.on(() => {
  j = b.getDelta(), F = b.getElapsedTime();
});
const ne = () => ({
  onBeforeLoop: te.on,
  onLoop: re.on,
  onAfterLoop: W.on,
  pause: ze,
  resume: Ie,
  isActive: Ge
}), dt = !0, K = "[TresJS ▲ ■ ●] ";
function O() {
  function e(r, n) {
    console.error(`${K} ${r}`, n || "");
  }
  function a(r) {
    console.warn(`${K} ${r}`);
  }
  function t(r, n) {
  }
  return {
    logError: e,
    logWarning: a,
    logMessage: t
  };
}
function vt(e) {
  return typeof e == "number" ? [e, e, e] : e instanceof he ? [e.x, e.y, e.z] : e;
}
function Ne(e) {
  return e instanceof H ? e : Array.isArray(e) ? new H(...e) : new H(e);
}
const ae = (e, a) => {
  for (const t of Object.keys(a))
    a[t] instanceof Object && Object.assign(a[t], ae(e[t], a[t]));
  return Object.assign(e || {}, a), e;
}, We = "html,body,base,head,link,meta,style,title,address,article,aside,footer,header,hgroup,h1,h2,h3,h4,h5,h6,nav,section,div,dd,dl,dt,figcaption,figure,picture,hr,img,li,main,ol,p,pre,ul,a,b,abbr,bdi,bdo,br,cite,code,data,dfn,em,i,kbd,mark,q,rp,rt,ruby,s,samp,small,span,strong,sub,sup,time,u,var,wbr,area,audio,map,track,video,embed,object,param,source,canvas,script,noscript,del,ins,caption,col,colgroup,table,thead,tbody,td,th,tr,button,datalist,fieldset,form,input,label,legend,meter,optgroup,option,output,progress,select,textarea,details,dialog,menu,summary,template,blockquote,iframe,tfoot", Ve = /* @__PURE__ */ qe(We);
function X(e) {
  return e && e.nodeType === 1;
}
function S(e) {
  return e.replace(/-([a-z])/g, (a, t) => t.toUpperCase());
}
function qe(e, a) {
  const t = /* @__PURE__ */ Object.create(null), r = e.split(",");
  for (let n = 0; n < r.length; n++)
    t[r[n]] = !0;
  return a ? (n) => !!t[n.toLowerCase()] : (n) => !!t[n];
}
const Ue = (e, a) => {
  const t = /* @__PURE__ */ new Set(), r = [];
  for (const n of e) {
    const o = a(n);
    t.has(o) || (t.add(o), r.push(n));
  }
  return r;
}, Y = (e, a) => {
  if (!a)
    return;
  const t = Array.isArray(a) ? a : a.match(/([^[.\]])+/g);
  return t == null ? void 0 : t.reduce((r, n) => r && r[n], e);
}, Je = (e, a, t) => {
  const r = Array.isArray(a) ? a : a.match(/([^[.\]])+/g);
  r && r.reduce((n, o, u) => (n[o] === void 0 && (n[o] = {}), u === r.length - 1 && (n[o] = t), n[o]), e);
};
function oe(e, a) {
  if (X(e) && X(a)) {
    const n = e.attributes, o = a.attributes;
    return n.length !== o.length ? !1 : Array.from(n).every(({ name: u, value: s }) => a.getAttribute(u) === s);
  }
  if (e === a)
    return !0;
  if (e === null || typeof e != "object" || a === null || typeof a != "object")
    return !1;
  const t = Object.keys(e), r = Object.keys(a);
  if (t.length !== r.length)
    return !1;
  for (const n of t)
    if (!r.includes(n) || !oe(e[n], a[n]))
      return !1;
  return !0;
}
function Ke(e, a) {
  if (!Array.isArray(e) || !Array.isArray(a) || e.length !== a.length)
    return !1;
  for (let t = 0; t < e.length; t++)
    if (!oe(e[t], a[t]))
      return !1;
  return !0;
}
const Xe = Array.isArray, Ye = Number.parseInt(Ee.replace("dev", "")), R = {
  realistic: {
    shadows: !0,
    physicallyCorrectLights: !0,
    outputColorSpace: _e,
    toneMapping: Me,
    toneMappingExposure: 3,
    shadowMap: {
      enabled: !0,
      type: Ae
    }
  }
};
function Qe({
  scene: e,
  canvas: a,
  options: t,
  disableRender: r,
  contextParts: { sizes: n, camera: o }
}) {
  const u = A(() => ({
    alpha: E(t.alpha),
    depth: E(t.depth),
    canvas: Te(a),
    context: E(t.context),
    stencil: E(t.stencil),
    antialias: E(t.antialias) === void 0 ? !0 : E(t.antialias),
    precision: E(t.precision),
    powerPreference: E(t.powerPreference),
    premultipliedAlpha: E(t.premultipliedAlpha),
    preserveDrawingBuffer: E(t.preserveDrawingBuffer),
    logarithmicDepthBuffer: E(t.logarithmicDepthBuffer),
    failIfMajorPerformanceCaveat: E(t.failIfMajorPerformanceCaveat)
  })), s = x(new $(u.value));
  ee(u, () => {
    s.value.dispose(), s.value = new $(u.value);
  }), D(() => {
    s.value.setSize(n.width.value, n.height.value);
  });
  const { pixelRatio: f } = Se();
  D(() => {
    s.value.setPixelRatio(f.value);
  });
  const { logError: i } = O(), c = (() => {
    const v = new $(), w = {
      shadowMap: {
        enabled: v.shadowMap.enabled,
        type: v.shadowMap.type
      },
      toneMapping: v.toneMapping,
      toneMappingExposure: v.toneMappingExposure,
      outputColorSpace: v.outputColorSpace
    };
    return v.dispose(), w;
  })();
  D(() => {
    const v = E(t.preset);
    v && (v in R || i(`Renderer Preset must be one of these: ${Object.keys(R).join(", ")}`), ae(s.value, R[v]));
    const w = (P, C) => {
      const k = E(P), T = () => {
        if (v)
          return Y(R[v], C);
      };
      if (k !== void 0)
        return k;
      const y = T();
      return y !== void 0 ? y : Y(c, C);
    }, h = (P, C) => Je(s.value, C, w(P, C));
    h(t.shadows, "shadowMap.enabled"), h(t.toneMapping, "toneMapping"), h(t.shadowMapType, "shadowMap.type"), Ye < 150 && h(!t.useLegacyLights, "physicallyCorrectLights"), h(t.outputColorSpace, "outputColorSpace"), h(t.toneMappingExposure, "toneMappingExposure");
    const M = w(t.clearColor, "clearColor");
    M && s.value.setClearColor(
      M ? Ne(M) : new H(0)
      // default clear color is not easily/efficiently retrievable from three
    );
  });
  const { pause: l, resume: p, onLoop: d } = ne();
  return d(() => {
    o.value && !E(r) && s.value.render(e, o.value);
  }), p(), N(() => {
    l(), s.value.dispose(), s.value.forceContextLoss();
  }), {
    renderer: s
  };
}
const Q = (e) => typeof e == "function", Ze = (e) => !!e && e.constructor === Array;
function et(e) {
  const a = { nodes: {}, materials: {} };
  return e && e.traverse((t) => {
    t.name && (a.nodes[t.name] = t), t.material && !a.materials[t.material.name] && (a.materials[t.material.name] = t.material);
  }), a;
}
async function gt(e, a, t, r, n) {
  const { logError: o } = O(), u = new e();
  n && n(u), t && t(u);
  const f = (Array.isArray(a) ? a : [a]).map(
    (i) => new Promise((m, c) => {
      u.load(
        i,
        (l) => {
          l.scene && Object.assign(l, et(l.scene)), m(l);
        },
        r,
        (l) => c(o("[useLoader] - Failed to load resource", l))
      );
    })
  );
  return Ze(a) ? await Promise.all(f) : await f[0];
}
async function yt(e) {
  const a = new ke(), t = new De(a), r = (n) => new Promise((o, u) => {
    t.load(
      n,
      (s) => o(s),
      () => null,
      () => {
        u(new Error("[useTextures] - Failed to load texture"));
      }
    );
  });
  if (Xe(e)) {
    const n = await Promise.all(e.map((o) => r(o)));
    return e.length > 1 ? n : n[0];
  } else {
    const {
      map: n,
      displacementMap: o,
      normalMap: u,
      roughnessMap: s,
      metalnessMap: f,
      aoMap: i,
      alphaMap: m,
      matcap: c
    } = e;
    return {
      map: n ? await r(n) : null,
      displacementMap: o ? await r(o) : null,
      normalMap: u ? await r(u) : null,
      roughnessMap: s ? await r(s) : null,
      metalnessMap: f ? await r(f) : null,
      aoMap: i ? await r(i) : null,
      alphaMap: m ? await r(m) : null,
      matcap: c ? await r(c) : null
    };
  }
}
const tt = (e, { renderer: a, camera: t, raycaster: r }) => {
  const n = A(() => a.value.domElement), { x: o, y: u } = Re({ target: n }), { width: s, height: f, top: i, left: m } = He(n), c = ({ x: y, y: _ }) => {
    if (n.value)
      return {
        x: (y - m.value) / s.value * 2 - 1,
        y: -((_ - i.value) / f.value) * 2 + 1
      };
  }, l = ({ x: y, y: _ }) => {
    if (t.value)
      return r.value.setFromCamera(new be(y, _), t.value), r.value.intersectObjects(e.value, !1);
  }, p = (y) => {
    const _ = c({
      x: (y == null ? void 0 : y.clientX) ?? o.value,
      y: (y == null ? void 0 : y.clientY) ?? u.value
    });
    return _ ? l(_) || [] : [];
  }, d = A(() => p()), v = B(), w = B(), h = (y, _) => {
    y.trigger({ event: _, intersects: p(_) });
  }, M = (y) => {
    h(w, y);
  };
  let P;
  const C = (y) => {
    var _;
    P = (_ = p(y)[0]) == null ? void 0 : _.object;
  }, k = (y) => {
    var _;
    y instanceof PointerEvent && P === ((_ = p(y)[0]) == null ? void 0 : _.object) && h(v, y);
  }, T = (y) => w.trigger({ event: y, intersects: [] });
  return n.value.addEventListener("pointerup", k), n.value.addEventListener("pointerdown", C), n.value.addEventListener("pointermove", M), n.value.addEventListener("pointerleave", T), N(() => {
    n != null && n.value && (n.value.removeEventListener("pointerup", k), n.value.removeEventListener("pointerdown", C), n.value.removeEventListener("pointermove", M), n.value.removeEventListener("pointerleave", T));
  }), {
    intersects: d,
    onClick: (y) => v.on(y).off,
    onPointerMove: (y) => w.on(y).off
  };
};
function wt() {
  const { logWarning: e } = O();
  function a(o, u, s) {
    let f = null;
    return o.traverse((i) => {
      i[u] === s && (f = i);
    }), f || e(`Child with ${u} '${s}' not found.`), f;
  }
  function t(o, u, s) {
    const f = [];
    return o.traverse((i) => {
      i[u].includes(s) && f.push(i);
    }), f.length || e(`Children with ${u} '${s}' not found.`), f;
  }
  function r(o, u) {
    return a(o, "name", u);
  }
  function n(o, u) {
    return t(o, "name", u);
  }
  return {
    seek: a,
    seekByName: r,
    seekAll: t,
    seekAllByName: n
  };
}
const rt = ({ scene: e, contextParts: a }) => {
  const t = se({
    click: /* @__PURE__ */ new Map(),
    pointerMove: /* @__PURE__ */ new Map(),
    pointerEnter: /* @__PURE__ */ new Map(),
    pointerLeave: /* @__PURE__ */ new Map()
  }), r = L(/* @__PURE__ */ new Set()), n = (l) => {
    r.value.add(l);
  }, o = (l) => {
    r.value.delete(l);
  }, u = (l) => {
    Object.values(t).forEach((p) => p.delete(l)), o(l);
  }, s = (l) => {
    const { onClick: p, onPointerMove: d, onPointerEnter: v, onPointerLeave: w } = l;
    p && t.click.set(l, p), d && t.pointerMove.set(l, d), v && t.pointerEnter.set(l, v), w && t.pointerLeave.set(l, w);
  };
  e.userData.tres__registerAtPointerEventHandler = s, e.userData.tres__deregisterAtPointerEventHandler = u, e.userData.tres__registerBlockingObjectAtPointerEventHandler = n, e.userData.tres__deregisterBlockingObjectAtPointerEventHandler = o;
  const f = A(
    () => Ue(
      [
        ...Array.from(r.value),
        ...Object.values(t).map((l) => Array.from(l.keys())).flat()
      ],
      ({ uuid: l }) => l
    )
  ), { onClick: i, onPointerMove: m } = tt(f, a);
  i(({ intersects: l, event: p }) => {
    var d;
    l.length && ((d = t.click.get(l[0].object)) == null || d(l[0], p));
  });
  let c;
  return m(({ intersects: l, event: p }) => {
    var M, P, C, k;
    const d = (M = l == null ? void 0 : l[0]) == null ? void 0 : M.object, { pointerLeave: v, pointerEnter: w, pointerMove: h } = t;
    c && c !== d && ((P = v.get(c)) == null || P(c, p)), d && (c !== d && ((C = w.get(d)) == null || C(l[0], p)), (k = h.get(d)) == null || k(l[0], p)), c = d || null;
  }), {
    registerObject: s,
    deregisterObject: u
  };
}, G = L({}), V = (e) => Object.assign(G.value, e);
function nt({
  scene: e,
  canvas: a,
  windowSize: t,
  disableRender: r,
  rendererOptions: n
}) {
  const o = A(
    () => E(t) ? je() : Fe(E(a).parentElement)
  ), u = A(() => o.value.width.value), s = A(() => o.value.height.value), f = A(() => u.value / s.value), i = {
    height: s,
    width: u,
    aspectRatio: f
  }, m = x(e), {
    camera: c,
    cameras: l,
    registerCamera: p,
    deregisterCamera: d,
    setCameraActive: v
  } = $e({ sizes: i, scene: e }), { renderer: w } = Qe(
    {
      scene: e,
      canvas: a,
      options: n,
      contextParts: { sizes: i, camera: c },
      disableRender: r
    }
  ), h = {
    sizes: i,
    scene: m,
    camera: c,
    cameras: ie(l),
    renderer: w,
    raycaster: x(new xe()),
    controls: L(null),
    extend: V,
    registerCamera: p,
    setCameraActive: v,
    deregisterCamera: d
  };
  return z("useTres", h), h;
}
function at() {
  const e = le("useTres");
  if (!e)
    throw new Error("useTresContext must be used together with useTresContextProvider");
  return e;
}
const Ct = at;
let g = null;
const { logError: Z } = O(), ot = {
  createElement(e, a, t, r) {
    var u, s;
    if (r || (r = {}), r.args || (r.args = []), e === "template" || Ve(e))
      return null;
    let n = e.replace("Tres", ""), o;
    if (e === "primitive") {
      (r == null ? void 0 : r.object) === void 0 && Z("Tres primitives need a prop 'object'");
      const f = r.object;
      n = f.type, o = Object.assign(f, { type: n, attach: r.attach, primitive: !0 });
    } else {
      const f = G.value[n];
      f || Z(`${n} is not defined on the THREE namespace. Use extend to add it to the catalog.`), o = new f(...r.args);
    }
    return o.isCamera && (r != null && r.position || o.position.set(3, 3, 3), r != null && r.lookAt || o.lookAt(0, 0, 0)), (r == null ? void 0 : r.attach) === void 0 && (o.isMaterial ? o.attach = "material" : o.isBufferGeometry && (o.attach = "geometry")), o.isObject3D && ((u = r == null ? void 0 : r.material) != null && u.isMaterial && (o.userData.tres__materialViaProp = !0), (s = r == null ? void 0 : r.geometry) != null && s.isBufferGeometry && (o.userData.tres__geometryViaProp = !0)), o.userData = {
      ...o.userData,
      tres__name: n
    }, o;
  },
  insert(e, a) {
    var r, n, o, u;
    a && a.isScene && (g = a);
    const t = a || g;
    if (e != null && e.isObject3D) {
      if (e != null && e.isCamera) {
        if (!(g != null && g.userData.tres__registerCamera))
          throw "could not find tres__registerCamera on scene's userData";
        (n = g == null ? void 0 : (r = g.userData).tres__registerCamera) == null || n.call(r, e);
      }
      if (e != null && e.onClick || e != null && e.onPointerMove || e != null && e.onPointerEnter || e != null && e.onPointerLeave) {
        if (!(g != null && g.userData.tres__registerAtPointerEventHandler))
          throw "could not find tres__registerAtPointerEventHandler on scene's userData";
        (u = g == null ? void 0 : (o = g.userData).tres__registerAtPointerEventHandler) == null || u.call(o, e);
      }
    }
    e != null && e.isObject3D && (t != null && t.isObject3D) ? (t.add(e), e.dispatchEvent({ type: "added" })) : e != null && e.isFog ? t.fog = e : typeof (e == null ? void 0 : e.attach) == "string" && (e.__previousAttach = e[t == null ? void 0 : t.attach], t && (t[e.attach] = e));
  },
  remove(e) {
    var a, t;
    if (e) {
      if (e.isObject3D) {
        const r = e, n = (i) => {
          var c, l;
          const m = i;
          i.userData.tres__materialViaProp || (c = m.material) == null || c.dispose(), i.userData.tres__geometryViaProp || (l = m.geometry) == null || l.dispose();
        }, o = g == null ? void 0 : g.userData.tres__deregisterAtPointerEventHandler, u = g == null ? void 0 : g.userData.tres__deregisterBlockingObjectAtPointerEventHandler, s = (i) => {
          var m, c;
          if (!u)
            throw "could not find tres__deregisterBlockingObjectAtPointerEventHandler on scene's userData";
          if ((c = g == null ? void 0 : (m = g.userData).tres__deregisterBlockingObjectAtPointerEventHandler) == null || c.call(m, i), !o)
            throw "could not find tres__deregisterAtPointerEventHandler on scene's userData";
          (i != null && i.onClick || i != null && i.onPointerMove || i != null && i.onPointerEnter || i != null && i.onPointerLeave) && (o == null || o(i));
        }, f = (i) => {
          const m = g == null ? void 0 : g.userData.tres__deregisterCamera;
          if (!m)
            throw "could not find tres__deregisterCamera on scene's userData";
          i.isCamera && (m == null || m(i));
        };
        r.traverse((i) => {
          n(i), f(i), s == null || s(i);
        }), n(r), f(r), s == null || s(r);
      }
      (a = e.removeFromParent) == null || a.call(e), (t = e.dispose) == null || t.call(e);
    }
  },
  patchProp(e, a, t, r) {
    var n, o, u, s;
    if (e) {
      let f = e, i = a;
      if (e.isObject3D && i === "blocks-pointer-events") {
        r || r === "" ? (o = g == null ? void 0 : (n = g.userData).tres__registerBlockingObjectAtPointerEventHandler) == null || o.call(n, e) : (s = g == null ? void 0 : (u = g.userData).tres__deregisterBlockingObjectAtPointerEventHandler) == null || s.call(u, e);
        return;
      }
      let m = S(i), c = f == null ? void 0 : f[m];
      if (i === "args") {
        const p = e, d = t ?? [], v = r ?? [], w = e.userData.tres__name || e.type;
        w && d.length && !Ke(d, v) && (f = Object.assign(p, new G.value[w](...r)));
        return;
      }
      if (f.type === "BufferGeometry") {
        if (i === "args")
          return;
        f.setAttribute(
          S(i),
          new Be(...r)
        );
        return;
      }
      if (i.includes("-") && c === void 0) {
        const p = i.split("-");
        c = p.reduce((d, v) => d[S(v)], f), i = p.pop(), m = i.toLowerCase(), c != null && c.set || (f = p.reduce((d, v) => d[S(v)], f));
      }
      let l = r;
      if (l === "" && (l = !0), Q(c)) {
        Array.isArray(l) ? e[m](...l) : e[m](l);
        return;
      }
      !(c != null && c.set) && !Q(c) ? f[m] = l : c.constructor === l.constructor && (c != null && c.copy) ? c == null || c.copy(l) : Array.isArray(l) ? c.set(...l) : !c.isColor && c.setScalar ? c.setScalar(l) : c.set(l);
    }
  },
  parentNode(e) {
    return (e == null ? void 0 : e.parent) || null;
  },
  createText: () => void 0,
  createComment: () => void 0,
  setText: () => void 0,
  setElementText: () => void 0,
  nextSibling: () => void 0,
  querySelector: () => void 0,
  setScopeId: () => void 0,
  cloneNode: () => void 0,
  insertStaticContent: () => void 0
}, { render: st } = ue(ot);
V(ye);
const it = ["data-scene"], lt = /* @__PURE__ */ q({
  __name: "TresCanvas",
  props: {
    shadows: { type: Boolean, default: void 0 },
    clearColor: {},
    toneMapping: {},
    shadowMapType: {},
    useLegacyLights: { type: Boolean, default: void 0 },
    outputColorSpace: {},
    toneMappingExposure: {},
    camera: {},
    preset: {},
    windowSize: { type: Boolean, default: void 0 },
    disableRender: { type: Boolean, default: void 0 },
    context: {},
    precision: {},
    alpha: { type: Boolean, default: void 0 },
    premultipliedAlpha: { type: Boolean },
    antialias: { type: Boolean, default: void 0 },
    stencil: { type: Boolean, default: void 0 },
    preserveDrawingBuffer: { type: Boolean, default: void 0 },
    powerPreference: {},
    depth: { type: Boolean, default: void 0 },
    logarithmicDepthBuffer: { type: Boolean, default: void 0 },
    failIfMajorPerformanceCaveat: { type: Boolean, default: void 0 }
  },
  setup(e, { expose: a }) {
    var l;
    const t = e, { logWarning: r } = O(), n = L(), o = x(new Le());
    ne();
    const u = ce(), s = (l = U()) == null ? void 0 : l.appContext.app, f = (p) => q({
      setup() {
        var v;
        const d = (v = U()) == null ? void 0 : v.appContext;
        return d && (d.app = s), z("useTres", p), z("extend", V), () => J(ge, null, u != null && u.default ? u.default() : []);
      }
    }), i = (p) => {
      const d = f(p);
      st(J(d), o.value);
    }, m = A(() => t.disableRender), c = x(null);
    return a({ context: c }), fe(() => {
      const p = n;
      c.value = nt({
        scene: o.value,
        canvas: p,
        windowSize: t.windowSize,
        disableRender: m,
        rendererOptions: t
      }), rt({ scene: o.value, contextParts: c.value });
      const { registerCamera: d, camera: v, cameras: w, deregisterCamera: h } = c.value;
      i(c.value);
      const M = () => {
        const P = new I(
          45,
          window.innerWidth / window.innerHeight,
          0.1,
          1e3
        );
        P.position.set(3, 3, 3), P.lookAt(0, 0, 0), d(P);
        const C = D(() => {
          w.value.length >= 2 && (P.removeFromParent(), h(P), C == null || C());
        });
      };
      ee(
        () => t.camera,
        (P, C) => {
          P && d(P), C && (C.removeFromParent(), h(C));
        },
        {
          immediate: !0
        }
      ), v.value || (r(
        "No camera found. Creating a default perspective camera. To have full control over a camera, please add one to the scene."
      ), M());
    }), (p, d) => (pe(), me("canvas", {
      ref_key: "canvas",
      ref: n,
      "data-scene": o.value.uuid,
      class: de(p.$attrs.class),
      style: ve({
        display: "block",
        width: "100%",
        height: "100%",
        position: p.windowSize ? "fixed" : "relative",
        top: 0,
        left: 0,
        pointerEvents: "auto",
        touchAction: "none",
        ...p.$attrs.style
      })
    }, null, 14, it));
  }
}), ut = [
  "TresCanvas",
  "TresLeches",
  "TresScene"
], ct = {
  template: {
    compilerOptions: {
      isCustomElement: (e) => e.startsWith("Tres") && !ut.includes(e) || e === "primitive"
    }
  }
}, Pt = ct, ht = {
  install(e) {
    e.component("TresCanvas", lt);
  }
};
export {
  lt as TresCanvas,
  G as catalogue,
  ht as default,
  V as extend,
  dt as isProd,
  Ne as normalizeColor,
  vt as normalizeVectorFlexibleParam,
  Pt as templateCompilerOptions,
  et as trasverseObjects,
  $e as useCamera,
  gt as useLoader,
  O as useLogger,
  rt as usePointerEventHandler,
  tt as useRaycaster,
  ne as useRenderLoop,
  Qe as useRenderer,
  wt as useSeek,
  yt as useTexture,
  Ct as useTres,
  at as useTresContext,
  nt as useTresContextProvider
};
